/**
 * ConnectionDroppedScreen Component
 * 
 * Shown when the WebSocket connection drops DURING an active conversation
 * (mid-session). This is distinct from the initial connection failure screen
 * (ErrorState/ConnectionErrorScreen) which handles cases where the connection
 * was never established.
 * 
 * This screen:
 * - Auto-saves the conversation transcript on mount
 * - Communicates clearly that the connection was lost (not that it failed to connect)
 * - Offers "Reconnect and Continue" which reloads the page, triggering the save/restore flow
 * - Offers "Save and Exit" as an alternative
 */

import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';

interface ConnectionDroppedScreenProps {
  onReconnect: () => void;
  onSaveAndExit: () => void;
  onAutoSave: () => Promise<void>;
  isSaving: boolean;
  hasMessages: boolean;
}

export default function ConnectionDroppedScreen({
  onReconnect,
  onSaveAndExit,
  onAutoSave,
  isSaving,
  hasMessages,
}: ConnectionDroppedScreenProps) {
  const { t } = useTranslation();
  const [autoSaveStatus, setAutoSaveStatus] = useState<'pending' | 'saving' | 'saved' | 'failed'>('pending');
  const autoSaveAttemptedRef = useRef(false);

  useEffect(() => {
    if (autoSaveAttemptedRef.current) return;
    if (!hasMessages) return;
    
    autoSaveAttemptedRef.current = true;
    setAutoSaveStatus('saving');
    
    onAutoSave()
      .then(() => setAutoSaveStatus('saved'))
      .catch(() => setAutoSaveStatus('failed'));
  }, [hasMessages, onAutoSave]);

  const isSaved = autoSaveStatus === 'saved';

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        
        {/* Icon */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full mb-4">
            <svg className="w-10 h-10 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('errors.connectionDropped.title', 'Connection Lost')}
          </h2>
          
          <p className="text-gray-600 text-base">
            {t('errors.connectionDropped.description', 'The connection was lost during your conversation. This can happen due to a temporary network issue.')}
          </p>
        </div>
        
        {/* Auto-save status */}
        {hasMessages && (
          <div className={`rounded-xl p-4 mb-6 ${
            autoSaveStatus === 'saved' ? 'bg-green-50 border border-green-200' :
            autoSaveStatus === 'failed' ? 'bg-red-50 border border-red-200' :
            'bg-blue-50 border border-blue-200'
          }`}>
            <div className="flex items-center gap-3">
              {autoSaveStatus === 'saving' && (
                <>
                  <div className="relative w-5 h-5 flex-shrink-0">
                    <div className="absolute inset-0 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                  </div>
                  <p className="text-blue-800 text-sm font-medium">
                    {t('errors.connectionDropped.saving', 'Saving your conversation...')}
                  </p>
                </>
              )}
              {autoSaveStatus === 'saved' && (
                <>
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-green-800 text-sm font-medium">
                    {t('errors.connectionDropped.saved', 'Your conversation has been saved.')}
                  </p>
                </>
              )}
              {autoSaveStatus === 'failed' && (
                <>
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                  <p className="text-red-800 text-sm font-medium">
                    {t('errors.connectionDropped.saveFailed', 'Could not save the conversation. Please try reconnecting.')}
                  </p>
                </>
              )}
              {autoSaveStatus === 'pending' && (
                <p className="text-blue-800 text-sm font-medium">
                  {t('errors.connectionDropped.saving', 'Saving your conversation...')}
                </p>
              )}
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={onReconnect}
            disabled={autoSaveStatus === 'saving' || isSaving}
            className="w-full px-6 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:bg-blue-400 transition-colors font-medium text-lg flex items-center justify-center gap-3 shadow-lg"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {isSaved 
              ? t('errors.connectionDropped.reconnect', 'Reconnect and Continue')
              : t('errors.connectionDropped.reconnectRetry', 'Try to Reconnect')
            }
          </button>
          
          <button
            onClick={onSaveAndExit}
            disabled={autoSaveStatus === 'saving' || isSaving}
            className="w-full px-6 py-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 disabled:bg-gray-50 transition-colors font-medium"
          >
            {isSaving
              ? t('scenario.session.saving', 'Saving...')
              : t('common.buttons.back', 'Go Back')
            }
          </button>
        </div>
        
        {/* Reassurance */}
        <p className="text-center text-sm text-gray-500 mt-4">
          {isSaved
            ? t('errors.connectionDropped.reassuranceSaved', 'Your conversation was saved. When you reconnect, you can continue where you left off.')
            : t('errors.connectionDropped.reassurance', 'This is usually a temporary issue. Try reconnecting.')
          }
        </p>
      </div>
    </div>
  );
}
